import { useEffect, useRef, useState } from 'react'
import { Navigate, useNavigate } from 'react-router-dom'
import {
  Camera,
  ChevronRight,
  ClipboardList,
  HardHat,
  LogOut,
  Truck,
} from 'lucide-react'
import { Button } from '@/core/ui/Button'
import { useAuth } from '@/features/auth/useAuth'
import { sessionStore, useIsAuthenticated, useSession } from '@/core/auth/SessionStore'
import { PickerSheet } from '@/features/common/PickerSheet'
import { SyncStatusIndicator } from '@/features/common/SyncStatusIndicator'
import { AndroidInstallButton } from '@/features/install/AndroidInstallButton'
import {
  useCarreteiroRecusadas,
  useOutboxCounts,
  useParteDiariaRecusadas,
  useProjeto,
  useProjetosComContagem,
} from '@/core/db/daos'
import { useSyncStatus } from '@/core/sync/useSyncStatus'
import { refreshAllCatalogs } from '@/core/sync/catalogSync'
import { aquecerOffline } from '@/core/sync/offlineWarmup'
import { OfflineReadyLine } from '@/features/common/OfflineReadyLine'
import { useLiveUpdateStore } from '@/features/liveUpdate/liveUpdateStore'
import { cn } from '@/lib/cn'

/**
 * Home — hub do app.
 *
 * Hierarquia visual:
 *  1. Top bar: wordmark + identificação do operador + sync + logout
 *  2. Card de projeto atual (CTA principal — abre picker)
 *  3. Linha de pendências (só renderiza se há outbox)
 *  4. Cards de módulos (Produção, Carreteiro, Parte Diária, Fotos) com badge de pendências
 *  5. Footnote: última sincronização + versão
 */
export function HomeRoute() {
  const authed = useIsAuthenticated()
  const session = useSession()
  const navigate = useNavigate()
  const { logout } = useAuth()

  const projetos = useProjetosComContagem()
  const projetoAtual = useProjeto(session?.projetoAtualId)
  const outbox = useOutboxCounts()
  const recusadas = useCarreteiroRecusadas()?.length ?? 0
  const recusadasPd = useParteDiariaRecusadas()?.length ?? 0
  const sync = useSyncStatus()

  const [showPicker, setShowPicker] = useState(false)
  const [catalogError, setCatalogError] = useState<string | null>(null)

  // Refresh de catálogos no boot e na volta ao foreground. Independente da
  // sync de outbox (que o engine cuida sozinho com heartbeat + eventos).
  useEffect(() => {
    if (!authed) return
    void refreshCatalogs()
    const onVisible = () => {
      if (document.visibilityState === 'visible') void refreshCatalogs()
    }
    document.addEventListener('visibilitychange', onVisible)
    return () => document.removeEventListener('visibilitychange', onVisible)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authed])

  async function refreshCatalogs() {
    setCatalogError(null)
    try {
      const summary = await refreshAllCatalogs()
      const falhas = Object.keys(summary.failed)
      if (falhas.length > 0 && summary.ok.length === 0) {
        setCatalogError('Sem conexão — usando catálogos em cache.')
      } else if (falhas.length > 0) {
        console.info('[home] catálogos parcial, falhou:', falhas.join(', '))
      }
    } catch (e) {
      setCatalogError(e instanceof Error ? e.message : 'Erro ao atualizar catálogos')
    }
  }

  function selecionarProjeto(id: number, codigo: string) {
    sessionStore.patch({ projetoAtualId: id, obraAtualId: null })
    void aquecerOffline()
    setShowPicker(false)
    console.info('[home] projeto selecionado:', codigo)
  }

  if (!authed) return <Navigate to="/login" replace />

  const hasProjeto = projetoAtual != null
  const totalProjetos = projetos?.length ?? 0

  return (
    <main className="min-h-svh bg-bg-base flex flex-col">
      {/* Top app bar — icon + nome do operador + indicador de sync + sair.
          Substituiu o botão manual "Sincronizar" (engine agora roda sozinho
          via heartbeat/events; tap no indicador força ciclo se quiser). */}
      <header className="sticky top-0 z-10 bg-bg-base/95 backdrop-blur border-b border-border pt-safe pl-safe pr-safe">
        <div className="flex items-center gap-3 px-4 py-3">
          <img
            src="/icon-source.png"
            alt="InfraWork"
            className="h-10 w-10 flex-shrink-0"
            decoding="async"
          />
          {session?.userNome && (
            <p className="flex-1 min-w-0 text-xs text-on-surface-dim truncate border-l border-border pl-3">
              {session.userNome}
            </p>
          )}
          <div className="flex items-center gap-1 flex-shrink-0">
            <AndroidInstallButton />
            <SyncStatusIndicator />
            <Button
              variant="ghost"
              size="icon"
              onClick={() => void logout()}
              aria-label="Sair"
            >
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </div>
        {catalogError && (
          <div className="px-4 pb-2 text-xs text-warn">{catalogError}</div>
        )}
      </header>

      <div className="flex-1 px-4 pt-5 pb-5 space-y-5 max-w-2xl mx-auto w-full">
        {/* Card de projeto atual — CTA principal */}
        <ProjetoAtualCard
          codigo={projetoAtual?.codigo}
          totalProjetos={totalProjetos}
          onPick={() => setShowPicker(true)}
        />

        {/* Linha de pendências (só renderiza se há outbox) */}
        {outbox && outbox.total > 0 && (
          <div className="flex items-center gap-2 px-1 text-xs text-on-surface-dim">
            <span className="h-1.5 w-1.5 rounded-full bg-warn animate-pulse" />
            <span>
              {outbox.producao > 0 && (
                <span className="text-warn font-semibold tabular">
                  {outbox.producao}
                </span>
              )}
              {[
                outbox.producao > 0 ? { n: outbox.producao, rotulo: ' produção' } : null,
                outbox.carreteiro > 0 ? { n: outbox.carreteiro, rotulo: ' ficha(s) de carreteiro' } : null,
                outbox.parteDiaria > 0 ? { n: outbox.parteDiaria, rotulo: ' parte(s) diária(s)' } : null,
                outbox.fotos > 0 ? { n: outbox.fotos, rotulo: ' foto(s)' } : null,
              ]
                .filter((p): p is { n: number; rotulo: string } => p != null)
                .map((p, i) => (
                  <span key={p.rotulo}>
                    {i > 0 && ' · '}
                    <span className="text-warn font-semibold tabular">{p.n}</span>
                    {p.rotulo}
                  </span>
                ))}
              {' aguardando sincronização'}
            </span>
          </div>
        )}

        <OfflineReadyLine temProjeto={hasProjeto} />

        {/* Cards de módulos */}
        <div className="space-y-3">
          <ModuleCard
            icon={<HardHat className="h-5 w-5" strokeWidth={1.8} />}
            title="Produção"
            subtitle="Apontamento de serviços executados"
            badge={outbox?.producao ?? 0}
            enabled={hasProjeto}
            onClick={() => navigate('/producao')}
          />
          <ModuleCard
            icon={<Truck className="h-5 w-5" strokeWidth={1.8} />}
            title="Carreteiro"
            subtitle="Ficha de viagens com carga e descarga"
            badge={outbox?.carreteiro ?? 0}
            alerta={recusadas > 0 ? `${recusadas} ficha(s) recusada(s) pelo servidor` : null}
            enabled={hasProjeto}
            onClick={() => navigate('/carreteiro')}
          />
          <ModuleCard
            icon={<ClipboardList className="h-5 w-5" strokeWidth={1.8} />}
            title="Parte Diária"
            subtitle="Horímetro e período de máquinas e equipamentos"
            badge={outbox?.parteDiaria ?? 0}
            alerta={recusadasPd > 0 ? `${recusadasPd} lançamento(s) recusado(s) pelo servidor` : null}
            enabled={hasProjeto}
            onClick={() => navigate('/parte-diaria')}
          />
          <ModuleCard
            icon={<Camera className="h-5 w-5" strokeWidth={1.8} />}
            title="Fotos"
            subtitle="Captura georreferenciada com marca d'água"
            badge={outbox?.fotos ?? 0}
            enabled={hasProjeto}
            onClick={() => navigate('/fotos')}
          />
        </div>
      </div>

      {/* Footer fixo — altura compacta (h-9 = 36px). pl/pr/pb-safe ficam fora
          do conteúdo: notch/homebar do iPhone preenche com a cor do footer
          sem aumentar a "barra" útil. Lê estado direto do useSyncStatus —
          mesma fonte que o SyncStatusIndicator no header. */}
      <footer className="sticky bottom-0 bg-bg-panel border-t border-border pl-safe pr-safe pb-safe">
        <div className="h-9 px-4 max-w-2xl mx-auto w-full flex items-center justify-between text-[11px] text-on-surface-muted font-mono tabular">
          <span className="flex items-center gap-1.5">
            <span
              className={cn(
                'h-1.5 w-1.5 rounded-full',
                sync.syncing
                  ? 'bg-info animate-pulse'
                  : sync.lastSyncAt
                    ? 'bg-ok'
                    : 'bg-on-surface-muted',
              )}
            />
            {sync.lastSyncAt
              ? `sync ${new Date(sync.lastSyncAt).toLocaleTimeString('pt-BR', {
                  hour: '2-digit',
                  minute: '2-digit',
                })}`
              : sync.syncing
                ? 'sincronizando…'
                : 'sync pendente'}
          </span>
          <VersionTag />
        </div>
      </footer>

      <PickerSheet
        open={showPicker}
        title="Selecionar projeto"
        items={projetos ?? []}
        label={(p) => p.codigo}
        sublabel={(p) =>
          p.qtdServicos > 0
            ? `${p.qtdServicos} serviços vinculados`
            : 'sem serviços cadastrados'
        }
        onPick={(p) => selecionarProjeto(p.id, p.codigo)}
        onDismiss={() => setShowPicker(false)}
        searchHint="Buscar por código"
        emptyMessage={
          totalProjetos === 0
            ? 'Catálogo de projetos vazio. Sincronize.'
            : 'Nenhum projeto encontrado'
        }
        emptyAction={
          totalProjetos === 0
            ? { label: 'Atualizar catálogos', onClick: refreshCatalogs }
            : undefined
        }
      />
    </main>
  )
}

// ----------------------------------------------------------------------------
//  Componentes locais — design "construção/infraestrutura" sério
// ----------------------------------------------------------------------------

/**
 * Tag de versão no footer. Tap longo (≥600ms) abre a tela de diagnóstico do
 * Live Update — útil em campo quando o suporte precisa entender por que o
 * dispositivo não atualizou. Ponto amarelo aparece se há bundle baixado
 * aguardando aplicação.
 */
function VersionTag() {
  const navigate = useNavigate()
  const pending = useLiveUpdateStore((s) => s.pendingUpdate)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const firedRef = useRef(false)

  function startLongPress() {
    firedRef.current = false
    timerRef.current = setTimeout(() => {
      firedRef.current = true
      navigate('/admin/live-update')
    }, 600)
  }
  function cancelLongPress() {
    if (timerRef.current) {
      clearTimeout(timerRef.current)
      timerRef.current = null
    }
  }

  return (
    <button
      type="button"
      onPointerDown={startLongPress}
      onPointerUp={cancelLongPress}
      onPointerLeave={cancelLongPress}
      onPointerCancel={cancelLongPress}
      onContextMenu={(e) => e.preventDefault()}
      className="inline-flex items-center gap-1.5 select-none"
      aria-label="Versão do app (toque longo para diagnóstico)"
    >
      <span>v0.1.14</span>
      {pending && (
        <span
          className="h-1.5 w-1.5 rounded-full bg-warn"
          aria-label="Atualização disponível"
        />
      )}
    </button>
  )
}

function ProjetoAtualCard({
  codigo,
  totalProjetos,
  onPick,
}: {
  codigo: string | undefined
  totalProjetos: number
  onPick: () => void
}) {
  const hasProjeto = codigo != null

  return (
    <button
      type="button"
      onClick={onPick}
      className={cn(
        'w-full text-left rounded-lg overflow-hidden relative group',
        'border border-border-strong bg-bg-panel',
        'transition-colors duration-150 ease-out',
        'hover:border-accent/60 active:border-accent',
      )}
    >
      {/* Faixa accent superior fina (acento construtivo) */}
      <div className="h-[2px] w-full bg-gradient-to-r from-accent/60 via-accent to-accent/60" />

      <div className="px-4 py-4 flex items-center gap-4">
        <div className="flex-1 min-w-0">
          <p className="text-[10px] font-semibold text-on-surface-dim uppercase tracking-[0.1em]">
            Projeto atual
          </p>
          <p
            className={cn(
              'mt-1.5 font-mono text-xl tabular',
              hasProjeto ? 'text-on-surface' : 'text-on-surface-muted italic',
            )}
          >
            {hasProjeto ? codigo : 'Selecionar projeto'}
          </p>
          <p className="text-[11px] text-on-surface-muted mt-1 tabular">
            {totalProjetos > 0
              ? `${totalProjetos} projeto(s) disponíveis`
              : 'Aguardando sincronização'}
          </p>
        </div>
        <div className="flex-shrink-0 h-9 w-9 rounded-md border border-border-strong flex items-center justify-center text-on-surface-dim group-hover:text-accent group-hover:border-accent/60 transition-colors">
          <ChevronRight className="h-4 w-4" />
        </div>
      </div>
    </button>
  )
}

interface ModuleCardProps {
  icon: React.ReactNode
  title: string
  subtitle: string
  badge: number
  /** Linha de alerta em vermelho (ex.: fichas recusadas pelo servidor). */
  alerta?: string | null
  enabled: boolean
  onClick: () => void
}

function ModuleCard({ icon, title, subtitle, badge, alerta, enabled, onClick }: ModuleCardProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!enabled}
      className={cn(
        'group w-full text-left relative rounded-md overflow-hidden',
        'border border-border bg-bg-panel',
        'flex items-stretch gap-0 min-h-[80px]',
        'transition-all duration-150 ease-out',
        enabled
          ? 'hover:border-border-strong hover:bg-bg-elevated active:bg-bg-elevated/80'
          : 'opacity-40 cursor-not-allowed',
      )}
    >
      {/* Barra accent à esquerda — acende no hover/active (estética desktop) */}
      <span
        aria-hidden
        className={cn(
          'w-[2px] bg-transparent transition-colors duration-150',
          enabled && 'group-hover:bg-accent group-active:bg-accent',
        )}
      />

      {/* Ícone em caixa com accent-glow */}
      <div className="flex items-center px-4">
        <div
          className={cn(
            'h-10 w-10 rounded-md flex items-center justify-center',
            'border border-accent/30 bg-accent-glow text-accent',
          )}
        >
          {icon}
        </div>
      </div>

      {/* Texto */}
      <div className="flex-1 flex flex-col justify-center py-3 pr-3 min-w-0">
        <div className="flex items-center gap-2">
          <h3 className="text-base font-semibold text-on-surface truncate">{title}</h3>
          {badge > 0 && (
            <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1.5 rounded-full bg-warn text-bg-base text-[10px] font-bold tabular">
              {badge > 99 ? '99+' : badge}
            </span>
          )}
        </div>
        <p className="text-xs text-on-surface-dim mt-0.5 truncate">{subtitle}</p>
        {alerta && <p className="text-xs text-bad font-medium mt-0.5 truncate">{alerta}</p>}
      </div>

      {/* Chevron à direita */}
      <div className="flex items-center pr-4 text-on-surface-muted">
        <ChevronRight className="h-4 w-4" />
      </div>
    </button>
  )
}
